import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Stack,
  Chip,
  Button,
  Grid,
  CircularProgress,
  Tooltip,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import useFetchWithMsal from "../../../hooks/useFetchWithMsal";
import { protectedResources } from "../../../authConfig";
import { managerDashboards } from "../../../config/managerDashboards";
import CustomSnackbar from "../../../components/CustomSnackbar";
import EditManagerAccessDialog from "./EditManagerAccessDialog";

const ACCENT = "#1976d2";

const getInitials = (user) => `${user.Name?.[0] || ""}${user.LastName?.[0] || ""}`.toUpperCase() || "?";

const ManagerUserCard = ({ user, onEdit }) => {
  const allowedLabels = managerDashboards.filter((d) => user.allowedDashboards.includes(d.key));

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${alpha("#000000", 0.06)}`,
        background: "linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12)",
          transform: "translateY(-4px)",
          borderColor: alpha(ACCENT, 0.4),
        },
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha("#f8fafc", 0.8)} 0%, ${alpha(ACCENT, 0.08)} 100%)`,
          p: 2.5,
          borderBottom: `2px solid ${alpha(ACCENT, 0.12)}`,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Avatar
          sx={{
            background: `linear-gradient(135deg, ${ACCENT} 0%, ${alpha(ACCENT, 0.8)} 100%)`,
            width: 44,
            height: 44,
            fontWeight: 700,
            boxShadow: `0 4px 12px ${alpha(ACCENT, 0.4)}`,
          }}
        >
          {getInitials(user)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap title={`${user.Name} ${user.LastName}`}>
            {user.Name} {user.LastName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap title={user.Email} sx={{ display: "block" }}>
            {user.Email}
          </Typography>
        </Box>
      </Box>

      <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", gap: 1.25 }}>
        {user.isSystemAdmin || user.isAdmin ? (
          <Tooltip title={user.isSystemAdmin ? "admin מלא במערכת — גישה מלאה תמיד" : "ADMIN לדשבורד המנהלים — כל הדשבורדים"}>
            <Chip
              icon={<VerifiedUserIcon />}
              label={user.isSystemAdmin ? "admin מלא" : "ADMIN — כל הדשבורדים"}
              size="small"
              sx={{ alignSelf: "flex-start", color: ACCENT, borderColor: ACCENT }}
              variant="outlined"
            />
          </Tooltip>
        ) : allowedLabels.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            אין גישה לדשבורדים
          </Typography>
        ) : (
          <Stack direction="row" gap={0.75} flexWrap="wrap">
            {allowedLabels.map((d) => (
              <Chip key={d.key} label={d.label} size="small" sx={{ bgcolor: alpha(ACCENT, 0.08), color: ACCENT, fontWeight: 600 }} />
            ))}
          </Stack>
        )}
      </CardContent>

      {!user.isSystemAdmin && (
        <CardActions sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
          <Button
            fullWidth
            variant="contained"
            size="small"
            startIcon={<EditOutlinedIcon />}
            onClick={() => onEdit(user)}
            sx={{
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 600,
              background: `linear-gradient(135deg, ${ACCENT} 0%, ${alpha(ACCENT, 0.85)} 100%)`,
            }}
          >
            עריכה
          </Button>
        </CardActions>
      )}
    </Card>
  );
};

const ManagerDashboardPermissionsPage = () => {
  const { execute } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.read });
  const [usersList, setUsersList] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingUserId, setEditingUserId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const backendUrl = process.env.REACT_APP_BACKEND_BASE_URL;

  const fetchUsers = async () => {
    try {
      const result = await execute("GET", `${backendUrl}/api/managerDashboard/permissions`);
      setUsersList(result);
    } catch (err) {
      setUsersList([]);
      setSnackbar({ open: true, message: "שגיאה בטעינת המשתמשים", severity: "error" });
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (user, draft) => {
    setSaving(true);
    try {
      await execute("PUT", `${backendUrl}/api/managerDashboard/permissions/${user._id}`, draft);
      setUsersList((prev) => prev.map((u) => (u._id === user._id ? { ...u, ...draft } : u)));
      setSnackbar({ open: true, message: "ההרשאות עודכנו בהצלחה", severity: "success" });
      setEditingUserId(null);
    } catch (err) {
      setSnackbar({ open: true, message: "שגיאה בעדכון ההרשאות", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!usersList) return [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return usersList;
    return usersList.filter(
      (u) =>
        u.Name?.toLowerCase().includes(term) ||
        u.LastName?.toLowerCase().includes(term) ||
        u.Email?.toLowerCase().includes(term),
    );
  }, [usersList, searchTerm]);

  if (!usersList) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={2.5}>
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />

      <Typography variant="h6">הרשאות דשבורד מנהלים</Typography>

      <Paper sx={{ p: 1.5, borderRadius: 3 }}>
        <TextField
          fullWidth
          placeholder="חיפוש לפי שם או אימייל..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {filteredUsers.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" py={4}>
          לא נמצאו משתמשים עם הרשאת כניסה לדשבורד המנהלים.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {filteredUsers.map((user) => (
            <Grid item xs={12} sm={6} md={4} key={user._id}>
              <ManagerUserCard user={user} onEdit={(u) => setEditingUserId(u._id)} />
            </Grid>
          ))}
        </Grid>
      )}

      <EditManagerAccessDialog
        user={usersList.find((u) => u._id === editingUserId) || null}
        open={!!editingUserId}
        onClose={() => setEditingUserId(null)}
        saving={saving}
        onSave={handleSave}
      />
    </Box>
  );
};

export default ManagerDashboardPermissionsPage;
